import { Injectable } from '@angular/core';
import { SnackbarService } from '@nagel-services';
import { ERROR_SNACKBAR, INFO_SNACKBAR, SnackbarInfoType, SUCCESS_SNACKBAR } from '@nagel-types';
import { WARNING_SNACKBAR } from 'libs/nagel-types/src/lib/snackbarTypes';
import { RESPONSE_CODES } from '@nagel-consts';
import { BatchLegResult, BatchLotLegResponse, LotLegResponse } from '../../models/assignmentsTypes';

export type BatchOutcomeMessages = {
    success: string;
    failure: string;
    partial: (succeededCount: number, totalCount: number) => string;
};

export type BatchLegMessages = {
    resolved: (legId: string) => string;
    failed: (legId: string, reason: string) => string;
};

const UNKNOWN_FAILURE_REASON = $localize`No reason was provided.`;

@Injectable({
    providedIn: 'root'
})
export class BatchLegResultService {
    constructor(private snackbarService: SnackbarService) { }

    toBatchLegResult = (legsResponse: BatchLotLegResponse[] | null | undefined): BatchLegResult => {
        const legs = legsResponse || [];
        const successCount = legs.filter((leg: BatchLotLegResponse) => leg.success).length;

        return {
            successCount,
            failedCount: legs.length - successCount,
            legsResponse: legs
        };
    };

    getTotalLegOperations =(result: BatchLegResult): number => result.successCount + result.failedCount;

    getConflictingLegs = (result: BatchLegResult): BatchLotLegResponse[] => {
        return (result.legsResponse || []).filter(leg => leg.statusCode === RESPONSE_CODES.CONFLICT);
    };

    getFailedLegs = (result: BatchLegResult): BatchLotLegResponse[] => {
        return (result.legsResponse || []).filter(leg => !leg.success && leg.statusCode !== RESPONSE_CODES.CONFLICT);
    };

    getOutcomeSnackbar = (result: BatchLegResult, messages: BatchOutcomeMessages): SnackbarInfoType => {
        const totalCount = this.getTotalLegOperations(result);

        if (result.successCount === totalCount) {
            return { message: messages.success, action: 'OK', type: SUCCESS_SNACKBAR };
        }

        if (result.successCount === 0) {
            return { message: messages.failure, action: 'OK', type: ERROR_SNACKBAR };
        }

        return {
            message: messages.partial(result.successCount, totalCount),
            action: 'OK',
            type: INFO_SNACKBAR
        };
    };

    getLegSnackbars = (result: BatchLegResult, messages: BatchLegMessages): SnackbarInfoType[] => {
        return (result.legsResponse || []).flatMap((leg: BatchLotLegResponse) => {
            if (leg.statusCode === RESPONSE_CODES.CONFLICT) {
                return [this.getConflictSnackbar(leg, messages)];
            }

            if (!leg.success) {
                return [this.getFailureSnackbar(leg, messages)];
            }

            return [];
        });
    };

    private getConflictSnackbar = (leg: BatchLotLegResponse, messages: BatchLegMessages): SnackbarInfoType => {
        if (leg.hasResolvedConflict) {
            return {
                message: messages.resolved(leg.legId),
                action: 'OK',
                type: WARNING_SNACKBAR,
                persistent: true
            };
        }

        return {
            message: $localize`Conflict detected on leg ${leg.legId}.
                Some local data could not be cleaned up. Our team has been notified. Log ID: ${leg.incidentId}.
                This Log ID will no longer be accessible after dismissing this message.`,
            action: 'OK',
            type: ERROR_SNACKBAR,
            persistent: true
        };
    };

    private getFailureSnackbar = (leg: BatchLotLegResponse, messages: BatchLegMessages): SnackbarInfoType => ({
        message: messages.failed(leg.legId, leg.error || UNKNOWN_FAILURE_REASON),
        action: 'OK',
        type: ERROR_SNACKBAR
    });

    handleBatchResult = (
        result: BatchLegResult,
        outcomeMessages: BatchOutcomeMessages,
        onSuccess?: () => void,
        legMessages?: BatchLegMessages
    ): void => {
        const outcomeSnackbar = this.getOutcomeSnackbar(result, outcomeMessages);
        const legSnackbars = legMessages ? this.getLegSnackbars(result, legMessages) : [];

        this.snackbarService.pushSnackbarSequence([outcomeSnackbar, ...legSnackbars]);

        if (outcomeSnackbar.type !== ERROR_SNACKBAR) {
            onSuccess && onSuccess();
        }
    };
}
